import React, { useEffect, useMemo, useRef, useState } from 'react';
import T from 'i18n';
import Grid from '@mui/material/Unstable_Grid2';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { FiltersStyles } from './FiltersStyles';
import classnames from 'classnames';
import { useFetchTags } from 'features/case/redux/fetchTags';
import isEqual from 'react-fast-compare';
import BookmarkButtonInput from './BookmarkButtonInput';
import ThreeStateDropDownFilters from './ThreeStateDropDownFilters';
import Bookmarks from './Bookmarks';
import DatePickers from './DatePickers';
import FilterOption from './FilterOption';
import { useSetFilterOptions } from 'features/case/redux/setFilterOptions';
import { selectFolders } from 'common/selectors';
import { useSelector } from 'react-redux';
import WorkerFactory from './WorkerFactory';
import myWorker from './worker';
import { removeIndexFolders } from 'utils/foldersStructuring';

export default ({
  filterHandler,
  defaultFilters,
  documents,
  columns,
  isAnnotation,
}: {
  filterHandler: any;
  defaultFilters: any;
  documents: any;
  columns: any;
  isAnnotation: boolean;
}) => {
  const classes = FiltersStyles();
  const [filterCategory, setFilterCategory] = useState(defaultFilters);
  const { fetchTags } = useFetchTags();
  const [loading, setLoading] = useState<null | boolean>(null);

  const defaultFilterOptions = [
    columns.includes('author') && {
      filterName: T.translate('filters.authors'),
      filterKey: 'authors',
    },
    columns.includes('recipient') && {
      filterName: T.translate('filters.recipients'),
      filterKey: 'recipients',
    },
    columns.includes('createdBy') && {
      filterName: T.translate('filters.createdBy'),
      filterKey: 'createdBy',
      valKey: 'id',
      labelKey: 'name',
    },
    columns.includes('tags') && {
      filterName: T.translate('filters.tags'),
      filterKey: 'tags',
      valKey: 'id',
      labelKey: 'name',
    },
    documents?.length > 0 &&
      documents[0].bundleLocations && {
        filterName: T.translate('filters.trialBundles'),
        filterKey: 'trialBundles',
        valKey: 'id',
        labelKey: 'name',
      },
    documents?.length > 0 &&
      documents[0].bundleLocations && {
        filterName: T.translate('filters.teamBundles') || 'Team Bundles',
        filterKey: 'teamBundles',
        valKey: 'id',
        labelKey: 'name',
      },
    documents?.length > 0 &&
      documents[0].bundleLocations && {
        filterName: T.translate('filters.privateBundles') || 'Private Bundles',
        filterKey: 'privateBundles',
        valKey: 'id',
        labelKey: 'name',
      },
    columns.includes('classifyType') && {
      filterName: T.translate('filters.classifyType'),
      filterKey: 'classifyType',
    },
    columns.includes('classifySubtype') && {
      filterName: T.translate('filters.classifySubtype'),
      filterKey: 'classifySubtype',
    },
  ].filter(Boolean);

  const { filterOptions, setFilterOptions } = useSetFilterOptions();

  const folders = useSelector(selectFolders);

  const trialBundlesMemo = useMemo(() => {
    const trialBundlesWithoutIndexFolder = removeIndexFolders(folders['trialbooks']);
    const addTrialbooksList =
      trialBundlesWithoutIndexFolder && trialBundlesWithoutIndexFolder.length > 0;
    const addTeamBundlesList =
      folders && folders['teamBundles'] && folders['teamBundles'].length > 0;
    const addPrivateBundlesList =
      folders && folders['privateBundles'] && folders['privateBundles'].length > 0;

    return [
      addTrialbooksList && { id: 'trialBundle', name: 'Trial Bundles' },
      ...(addTrialbooksList ? trialBundlesWithoutIndexFolder : []),
      addTeamBundlesList && { id: 'teamBundles', name: 'Team Bundles' },
      ...(addTeamBundlesList ? folders['teamBundles'] : []),
      addPrivateBundlesList && { id: 'privateBundles', name: 'Private Bundles' },
      ...(addPrivateBundlesList ? folders['privateBundles'] : []),
    ].filter(Boolean);
  }, [folders]);

  const options = filterOptions || defaultFilterOptions;

  const clearFilter = {
    authors: [],
    createdBy: [],
    classifyType: [],
    classifySubtype: [],
    dateFilter: false,
    dateFrom: null,
    dateTo: null,
    dateUploadedFilter: false,
    dateUploadedFrom: null,
    dateUploadedTo: null,
    goToGlobalPageIdx: defaultFilters.goToGlobalPageIdx,
    private: false,
    public: false,
    recipients: [],
    tags: [],
    trialBundles: [],
    teamBundles: [], // Add this
    privateBundles: [], // Add this
    withAnnotations: false,
    withPublicHyperlinks: false,
    withoutAnnotations: false,
    withoutPublicHyperlinks: false,
    shared: false,
    notShared: false,
    withFlag: false,
    noFlag: false,
    withMerged: false,
    notMerged: false,
    ...options.reduce((acc: any, fo: any) => ({ ...acc, [fo.filterKey]: [] }), {}),
  };

  const setFilters = (newFilterCategory: any) => {
    // Merge with clearFilter to ensure all filter properties exist with default values
    const mergedFilters = { ...clearFilter, ...newFilterCategory };
    setFilterCategory(mergedFilters);
    filterHandler(mergedFilters);
  };

  const clearFilters = () => {
    setFilters(clearFilter);
  };

  // Ensure filterCategory is properly initialized with all required properties
  useEffect(() => {
    if (filterCategory) {
      const mergedFilters = { ...clearFilter, ...filterCategory };
      if (JSON.stringify(mergedFilters) !== JSON.stringify(filterCategory)) {
        setFilterCategory(mergedFilters);
      }
    }
  }, []); // Run only once on mount

  const [worker, setWorker] = useState<any>(null);

  useEffect(() => {
    const workerInstance = new WorkerFactory(myWorker) as any;

    // Set up event listener for messages from the worker
    workerInstance.onmessage = (event: any) => {
      setFilterOptions(event.data);
      setLoading(false);
    };

    // Save the worker instance to state
    setWorker(workerInstance);

    // Clean up the worker when the component unmounts
    return () => {
      workerInstance.terminate();
    };
  }, []); // Run this effect only once when the component mounts

  useEffect(() => {
    if (worker) {
      setLoading(true);
      fetchTags().then((tagsFetched: any) => {
        setLoading(true);
        worker.postMessage({
          documents,
          filterOptions,
          trialBundlesMemo,
          tagsFetched,
          defaultFilterOptions,
        });
      });
    }
  }, [documents, columns, worker]);

  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <div className={classes.paper} ref={containerRef}>
      <div className={classes.listSubheaderHeading}>
        {T.translate('filters.filters')}
        <div style={{ display: 'flex' }}>
          {!isEqual(filterCategory, clearFilter) && (
            <BookmarkButtonInput isAnnotation={isAnnotation} filterCategory={filterCategory} />
          )}
          <div style={{ margin: '0.5rem' }} />
          <Button
            variant="contained"
            size="small"
            color="primary"
            className={classnames(classes.clearButton)}
            onClick={clearFilters}
          >
            {T.translate('filters.clear')}
          </Button>
        </div>
      </div>
      <Divider />
      <PerfectScrollbar style={{ maxHeight: '75vh', padding: '0 1rem' }}>
        <Grid container spacing={2} style={{ width: '100%', margin: 0 }}>
          {columns && columns.includes('docDate') && (
            <DatePickers filterCategory={filterCategory} setFilters={setFilters} />
          )}
          {columns?.includes('dateUploaded') && (
            <DatePickers
              filterCategory={filterCategory}
              setFilters={setFilters}
              label={T.translate('filters.dateUploaded')}
              fieldPrefix="dateUploaded"
            />
          )}
          {options.map((option: any, idx: number) =>
            option.originalArray && option.originalArray.length > 0 ? (
              <FilterOption
                key={option.filterName}
                idx={idx}
                originalArray={option.originalArray}
                filterName={option.filterName}
                filterKey={option.filterKey}
                valKey={option.valKey}
                labelKey={option.labelKey}
                setFilters={setFilters}
                filterCategory={filterCategory}
                loading={loading}
                placeholder={
                  [
                    'trialBundles',
                    'teamBundles',
                    'privateBundles',
                    'authors',
                    'recipients',
                    'classifyType',
                    'classifySubtype',
                  ].includes(option.filterKey)
                    ? T.translate('filters.selectSearchLabel')
                    : ''
                }
              />
            ) : null,
          )}
          <ThreeStateDropDownFilters
            isAnnotation={isAnnotation}
            filterCategory={filterCategory}
            setFilters={setFilters}
          />
        </Grid>
      </PerfectScrollbar>
      <Bookmarks isAnnotation={isAnnotation} setFilters={setFilters} containerRef={containerRef} />
    </div>
  );
};
